import React, { memo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useTrendState } from '../../../../../hooks/useTrendState.js';
import { LoadingSpinner, ErrorMessage, NoData } from '../../../../../components/common/UI.js';
import { arePropsEqual } from '../../../../../utils/propsComparison.js';
import FeatureAnalysisChart from '../product-analysis/FeatureAnalysisChart.js';
import ColorAnalysisWrapper from '../product-analysis/ColorAnalysisWrapper.js';
import ProductListChart from '../product-analysis/ProductListChart.js';
import ChartContainer from '../common/ChartContainer.js';

export const ProductAnalysisTab = memo(({ filterData, tabKey, shouldFetch, preloadedData }) => {
  useLanguage(); // Initialize language context

  // Always call the hook unconditionally - only the shouldFetch parameter is conditional
  const hookResult = useTrendState({
    filterData,
    tabKey,
    shouldFetch: shouldFetch && !preloadedData
  });

  const {
    data: fetchedData,
    isLoading,
    error,
    retry
  } = hookResult;

  // Use preloaded data if available, otherwise use fetched data
  const trendData = preloadedData || fetchedData;

  const handleResetFilters = () => {
    window.location.reload();
  };

  if (!preloadedData && isLoading) return <LoadingSpinner />;
  if (!preloadedData && error) return <ErrorMessage error={error} onRetry={retry} onResetFilters={handleResetFilters} />;
  if (!trendData) return <NoData />;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '12px',
      padding: '0',
      width: '100%',
      marginBottom: '0'
    }}>
      {/* Row 1: Feature Analysis Chart - Full width */}
      <div style={{ gridColumn: 'span 6', gridRow: '1' }}>
        <FeatureAnalysisChart data={trendData.data} />
      </div>

      {/* Row 2: Color Analysis Chart - 1/3 width */}
      <div style={{ gridColumn: 'span 2', gridRow: '2' }}>
        <ColorAnalysisWrapper data={trendData.data} />
      </div>

      {/* Row 2: Product List Chart - 2/3 width (next to Color Analysis) */}
      <div style={{ gridColumn: 'span 4', gridRow: '2' }}>
        <ChartContainer autoHeight={false} height="670px" style={{ overflow: 'visible' }}>
          <ProductListChart data={trendData.data} />
        </ChartContainer>
      </div>
    </div>
  );
}, arePropsEqual);
